import {useCallback, useState} from 'react';
import {ScrollView, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {Button, IconButton} from 'react-native-paper';
import {Text, Touchable} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import {
  ProviderAssessmentRecord,
  buildAssessmentTrend,
  buildProviderActionLabel,
  loadCompletedAssessments,
} from '../../Utils/Assessment';
import {clearProviderSession, loadProviderSession} from '../../Utils/ProviderSession';
import {RootProviderDashboardScreenProps} from '../../Typings/NavigationTypes';
import getStyles from './ADAPT-MH.styles';

type Props = RootProviderDashboardScreenProps;

const ProviderDashboard: React.FC<Props> = ({navigation}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [records, setRecords] = useState<ProviderAssessmentRecord[]>([]);

  useFocusEffect(
    useCallback(() => {
      const loadState = async () => {
        const providerSession = await loadProviderSession();
        if (!providerSession) {
          navigation.replace('ProviderLogin');
          return;
        }

        setRecords(await loadCompletedAssessments());
      };

      loadState();
    }, [navigation]),
  );

  const onLogoutPress = useCallback(async () => {
    await clearProviderSession();
    navigation.replace('ProviderLogin');
  }, [navigation]);

  const summaryCountLabel =
    records.length === 1
      ? '1 shared summary on this device.'
      : `${records.length} shared summaries on this device.`;

  return (
    <PageContainer withSafeArea withPadding>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.hero}>
          <View style={styles.row}>
            <Text fontSize={30} lineHeight={37} weight="black" style={styles.title}>
              Provider dashboard
            </Text>
            <IconButton
              accessibilityLabel="Log out of provider view"
              icon="logout"
              iconColor={theme.colors.error}
              size={24}
              onPress={onLogoutPress}
            />
          </View>
        </View>

        {records.length === 0 ? (
          <View style={styles.card}>
            <Text weight="bold" fontSize={18} style={styles.strongBody}>
              No shared summaries yet.
            </Text>
            <Text style={styles.listItem}>
              Complete a self-check first, then return here to view the provider summary.
            </Text>
            <Button
              mode="contained"
              onPress={() => navigation.navigate('Landing')}
              style={styles.button}
              buttonColor={theme.colors.primary}
              textColor={theme.colors.textContrast}
            >
              Back to start
            </Button>
          </View>
        ) : (
          <View style={styles.softCard}>
            <Text weight="bold" fontSize={17} style={styles.strongBody}>
              Shared summaries
            </Text>
            <Text style={styles.listItem}>{summaryCountLabel}</Text>
          </View>
        )}

        {records.map((record) => {
          const {resultSummary} = record;
          const isUrgent = resultSummary.riskFlag.triggered;
          const canViewSummary = record.sharingConsent !== 'private';
          const relatedRecords = record.participantAlias
            ? records.filter((item) => item.participantAlias === record.participantAlias)
            : [record];
          const assessmentTrend = buildAssessmentTrend(record, relatedRecords);
          const cardContent = (
            <View style={[styles.card, isUrgent ? styles.urgentCard : null]}>
              <View style={styles.row}>
                <Text weight="bold" fontSize={17} style={styles.strongBody}>
                  {canViewSummary
                    ? resultSummary.headline
                    : 'Private assessment summary not shared'}
                </Text>
              </View>
              <Text style={styles.listItem}>
                {new Date(resultSummary.completedAt).toLocaleString()}
              </Text>
              <Text style={styles.listItem}>Mode: {record.mode}</Text>
              <Text style={styles.listItem}>
                Sharing consent: {record.sharingConsent || 'not specified'}
              </Text>
              {canViewSummary ? (
                <>
                  <Text style={styles.listItem}>Risk flag: {isUrgent ? 'Urgent' : 'None'}</Text>
                  <Text style={styles.listItem}>{assessmentTrend.label}</Text>
                  <Text style={styles.listItem}>
                    Suggested action: {buildProviderActionLabel(resultSummary)}
                  </Text>
                </>
              ) : (
                <Text style={styles.listItem}>
                  Provider details are hidden for this local demo consent setting.
                </Text>
              )}
            </View>
          );

          return canViewSummary ? (
            <Touchable
              key={record.sessionId}
              onPress={() =>
                navigation.navigate('ProviderAssessment', {sessionId: record.sessionId})
              }
            >
              {cardContent}
            </Touchable>
          ) : (
            <View key={record.sessionId}>{cardContent}</View>
          );
        })}
      </ScrollView>
    </PageContainer>
  );
};

export default ProviderDashboard;
